import axios, { AxiosResponse } from 'axios';
import { ID, Response } from '../../../../../_metronic/helpers';
// import { uuid } from '../../../../../_metronic/helpers/crud-helper/models';

import {
  Client,
  ClientActivityEntry,
  ClientsQueryResponse,
  ClientInvite,
  ClientOneOffPayment,
  ClientProvider,
  ClientProvidersQueryResponse,
  ClientSubscription,
  ClientSubscriptionQueryResponse,
  Guardian,
  GuardiansQueryResponse,
  IClientBalanceCredit,
  IBalanceCreditDetachResponse,
  IClientSessionCredit,
  ClientContact,
  ClientContactConsent,
} from './_models';
import {
  CLIENT_PROVIDER_URL,
  CLIENT_SUBSCRIPTIONS_URL,
  CLIENT_URL,
  GUARDIAN_URL,
  API_URL,
  CLIENT_CONTACT_URL,
} from '../../../../helpers/api';

import { ISuperBill, Provider } from '../../../../helpers/models';
import type { BulkActionName, BulkActionResult } from '../client-details/creditBulk';
import { normalizePlanIntervals } from '../client-details/planEdit';
import { useQuery } from 'react-query';
import { monitoringCaptureError } from '../../../../helpers/monitoring';
import { AnyCnameRecord } from 'dns';

/**
 * Write payloads may carry an explicit `null` phone to CLEAR a stored number (WST-858).
 *
 * The read models keep `phone?: string`, because `undefined` on the way out means
 * "the API did not send one". On the way in it has to mean something else: these are DRF
 * partial updates, so an absent key leaves the stored value untouched. Since
 * `JSON.stringify` (axios' default request transform) drops `undefined`-valued keys
 * entirely, "clear this field" can only be expressed as an explicit `null`.
 */
type ClientWrite = Omit<Client, 'phone'> & { phone?: string | null };
type GuardianWrite = Omit<Guardian, 'phone'> & { phone?: string | null };

const getClients = (query: string, onError?: (error?: any) => void): Promise<ClientsQueryResponse | undefined> => {
  return axios
    .get(`${CLIENT_URL}?${query}`)
    .then((d: AxiosResponse<ClientsQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClients',
        data: { query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientProviders = (
  query: string,
  onError?: (error?: any) => void
): Promise<ClientProvidersQueryResponse | undefined> => {
  return axios
    .get(`${CLIENT_PROVIDER_URL}?${query}`)
    .then((d: AxiosResponse<ClientProvidersQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientProviders',
        data: { query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientById = (clientUuid: ID, onError?: (error?: any) => void): Promise<Client | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/`)
    .then((response: AxiosResponse<Client>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientById',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

// Siblings API
const getClientSiblings = (
  clientUuid: ID,
  onError?: (error?: any) => void
): Promise<Array<Client> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/siblings/`)
    .then((d: AxiosResponse<Array<Client>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSiblings',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const linkClientSibling = (
  clientUuid: ID,
  siblingUuid: ID,
  providerUuid?: ID,
  onError?: (error?: any) => void
): Promise<boolean> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/siblings/`, { uuid: siblingUuid, ...(providerUuid ? { provider_uuid: providerUuid } : {}) })
    .then(() => true)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'linkClientSibling',
        data: { clientUuid, siblingUuid, providerUuid },
      });
      if (onError) onError(error);
      else throw error;
      return false;
    });
};

const unlinkClientSibling = (
  clientUuid: ID,
  siblingUuid: ID,
  onError?: (error?: any) => void
): Promise<boolean> => {
  return axios
    .delete(`${CLIENT_URL}${clientUuid}/siblings/`, { data: { uuid: siblingUuid } })
    .then(() => true)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'unlinkClientSibling',
        data: { clientUuid, siblingUuid },
      });
      if (onError) onError(error);
      else throw error;
      return false;
    });
};

const createClientSibling = (
  clientUuid: ID,
  sibling: Partial<Client>,
  providerUuid?: ID,
  onError?: (error?: any) => void
): Promise<Client | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/siblings/`, { client: sibling, ...(providerUuid ? { provider_uuid: providerUuid } : {}) })
    .then((response: AxiosResponse<Client>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientSibling',
        data: { clientUuid, sibling, providerUuid },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const useClientById = (clientUuid?: ID, onError?: (error?: any) => void) => {
  return useQuery(
    ['clients', clientUuid],
    async (): Promise<Client> => {
      const { data } = await axios.get(`${API_URL}/clients/${clientUuid}/`);
      return data;
    },
    {
      enabled: !!clientUuid,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'client._requests',
          func: 'getClientById',
          data: { clientUuid },
        });
        if (onError) onError(error);
        else throw error;

        return undefined;
      },
    }
  );
};

const createClient = (client: ClientWrite, onError?: (error?: any) => void): Promise<Client | undefined> => {
  return axios
    .post(CLIENT_URL, client)
    .then((response: AxiosResponse<Client>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClient',
        data: { client: client?.uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateClient = (client: ClientWrite, onError?: (error?: any) => void): Promise<Client | undefined> => {
  return axios
    .patch(`${CLIENT_URL}${client.uuid}/`, client)
    .then((response: AxiosResponse<Client>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClient',
        data: { client: client?.uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const archiveClientProvider = (
  clientProviderUuid: ID,
  onError?: (error?: any) => void
): Promise<ClientProvider | undefined> => {
  return axios
    .post(`${CLIENT_PROVIDER_URL}${clientProviderUuid}/archive/`)
    .then((response: AxiosResponse<ClientProvider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'archiveClientProvider',
        data: { clientProviderUuid: clientProviderUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteClientProvider = (clientProviderUuid: ID, onError?: (error?: any) => void): Promise<void> => {
  return axios
    .delete(`${CLIENT_PROVIDER_URL}${clientProviderUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'deleteClient',
        data: { clientProviderUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const reassignClientProvider = (
  clientProvider: ClientProvider,
  newProvider: Provider,
  archive: boolean,
  onError?: (error?: any) => void
): Promise<Client | undefined> => {
  return axios
    .post(`${CLIENT_PROVIDER_URL}${clientProvider.uuid}/reassign/`, { new_provider: newProvider.uuid, archive })
    .then((response: AxiosResponse<Client>) => {
      // Backend NotFound200 answers HTTP 200 with {"error": ...}; surface it as a failure.
      if ((response.data as any)?.error) throw { response };
      return response.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'reassignClient',
        data: { clientProvider: clientProvider.uuid, new_provider: newProvider.uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

// Get ClientProvider UUID for a given client and provider
const getClientProviderId = (
  clientUuid: ID,
  providerUuid: ID,
  onError?: (error?: any) => void
): Promise<string | null | undefined> => {
  return axios
    .get(`${CLIENT_PROVIDER_URL}get-id`, { params: { client: clientUuid, provider: providerUuid } })
    .then((res) => res.data?.uuid || null)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientProviderId',
        data: { clientUuid, providerUuid },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const deleteSelectedClientProviders = (
  clientProviderUuids: Array<ID>,
  onError?: (error?: any) => void
): Promise<void> => {
  const requests = clientProviderUuids.map((uuid) =>
    axios.delete(`${CLIENT_PROVIDER_URL}${uuid}/`).catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'deleteSelectedClients',
        data: { clientProviderUuids, uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    })
  );
  return axios.all(requests).then(() => {});
};

const archiveSelectedClientProviders = (
  clientProviderUuids: Array<ID>,
  onError?: (error?: any) => void
): Promise<void> => {
  const requests = clientProviderUuids.map((uuid) =>
    axios.post(`${CLIENT_PROVIDER_URL}${uuid}/archive/`).catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'archiveSelectedClientProviders',
        data: { clientProviderUuids, uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    })
  );
  return axios.all(requests).then(() => {});
};

const bulkUpdateClientProviderTags = (
  clientProviderUuids: Array<ID>,
  addTagUuids: Array<string>,
  removeTagUuids: Array<string>,
  onError?: (error?: any) => void
): Promise<{ updated: number } | undefined> => {
  return axios
    .post(`${CLIENT_PROVIDER_URL}bulk-tags/`, {
      client_provider_uuids: clientProviderUuids,
      add_tag_uuids: addTagUuids,
      remove_tag_uuids: removeTagUuids,
    })
    .then((res: AxiosResponse<{ updated: number }>) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'bulkUpdateClientProviderTags',
        data: { clientProviderUuids, addTagUuids, removeTagUuids },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const getGuardians = (
  clientUuid: ID,
  query: string,
  onError?: (error?: any) => void
): Promise<GuardiansQueryResponse | undefined> => {
  return axios
    .get(`${CLIENT_URL}/${clientUuid}/guardians?${query}`)
    .then((d: AxiosResponse<GuardiansQueryResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getGuardians',
        data: { clientUuid, query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getGuardianById = (guardianUuid: ID, onError?: (error?: any) => void): Promise<Guardian | undefined> => {
  return axios
    .get(`${GUARDIAN_URL}${guardianUuid}/`)
    .then((response: AxiosResponse<Guardian>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getGuardianById',
        data: { guardianUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createGuardian = (
  clientUuid: ID,
  guardian: GuardianWrite,
  onError?: (error?: any) => void
): Promise<Guardian | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/guardians/`, guardian)
    .then((response: AxiosResponse<Guardian>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createGuardian',
        data: { clientUuid, guardian },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateGuardian = (guardian: GuardianWrite, onError?: (error?: any) => void): Promise<Guardian | undefined> => {
  return axios
    .patch(`${GUARDIAN_URL}${guardian.uuid}/`, guardian)
    .then((response: AxiosResponse<Guardian>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateGuardian',
        data: { guardian: guardian?.uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteGuardian = (guardianUuid: ID, onError?: (error?: any) => void): Promise<void> => {
  return axios
    .delete(`${GUARDIAN_URL}${guardianUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'deleteGuardian',
        data: { guardianUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

// Unlink guardian from a single client (does not delete guardian)
const removeClientGuardian = (
  clientUuid: ID,
  guardianUuid: ID,
  onError?: (error?: any) => void
): Promise<void> => {
  return axios
    .delete(`${CLIENT_URL}${clientUuid}/guardians/${guardianUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'removeClientGuardian',
        data: { clientUuid, guardianUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const deleteSelectedGuardians = (guardianUuids: Array<ID>, onError?: (error?: any) => void): Promise<void> => {
  const requests = guardianUuids.map((uuid) =>
    axios.delete(`${GUARDIAN_URL}${uuid}/`).catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'deleteSelectedGuardians',
        data: { guardianUuids, uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    })
  );
  return axios.all(requests).then(() => {});
};

const updateClientProvider = (
  clientProvider: ClientProvider,
  onError?: (error?: any) => void
): Promise<ClientProvider | undefined> => {
  return axios
    .patch(`${CLIENT_PROVIDER_URL}${clientProvider.uuid}/`, clientProvider)
    .then((response: AxiosResponse<ClientProvider>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClientProvider',
        data: { clientProvider },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientSubscriptions = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<ClientSubscription> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/subscriptions/?${query || ''}`)
    .then((d: AxiosResponse<Array<ClientSubscription>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSubscriptions',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getAllClientSubscriptions = (
  filters?: any,
  onError?: (error?: any) => void
): Promise<Array<ClientSubscription> | undefined> => {
  return axios
    .get(`${API_URL}/client-subscriptions/`, { params: filters })
    .then((d: AxiosResponse<Array<ClientSubscription>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getAllClientSubscriptions',
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

type MeteredSessionEligibilityResponse = {
  has_metered_subscription: boolean;
  subscription_uuid: string | null;
  can_attach_to_existing_invoice: boolean;
  late_entry: boolean | null;
};

const checkMeteredSessionEligibility = (
  params: { clientUuid: ID; providerUuid: ID; providerServiceUuid: ID; start?: Date },
  onError?: (error?: any) => void
): Promise<MeteredSessionEligibilityResponse | undefined> => {
  return axios
    .post(`${CLIENT_SUBSCRIPTIONS_URL}metered-session-eligibility/`, {
      client: params.clientUuid,
      provider: params.providerUuid,
      provider_service: params.providerServiceUuid,
      start: params.start ? params.start.toISOString() : undefined,
    })
    .then((res: AxiosResponse<MeteredSessionEligibilityResponse>) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'checkMeteredSessionEligibility',
        data: { params },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const reassignClientSubscription = (
  clientSubscription: ClientSubscription,
  provider: Provider,
  onError?: (error?: any) => void
): Promise<ClientSubscription | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientSubscription.client?.uuid}/subscriptions/${clientSubscription.uuid}/reassign/`, {
      provider: { uuid: provider.uuid },
    })
    .then((d: AxiosResponse<ClientSubscription>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'reassignClientSubscriptions',
        data: { provider: provider.uuid, clientSubscription: clientSubscription.uuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

function useClientSubscriptions(clientUuid?: ID, query?: string, enabled?: boolean, onError?: (error?: any) => void) {
  return useQuery(
    ['client-subscriptions', clientUuid, query],
    async (): Promise<Array<ClientSubscription> | undefined> => {
      return await getClientSubscriptions(clientUuid, query);
    },
    {
      enabled: enabled !== false && !!clientUuid,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useNotes',
          data: { client: clientUuid, enabled },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

const getClientSubscriptionById = (
  clientSubscriptionUuid: ID,
  onError?: (error?: any) => void
): Promise<ClientSubscription | undefined> => {
  return axios
    .get(`${CLIENT_SUBSCRIPTIONS_URL}${clientSubscriptionUuid}/`)
    .then((response: AxiosResponse<ClientSubscription>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSubscriptionById',
        data: { clientSubscriptionUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientSubscriptionCount = (onError?: (error?: any) => void): Promise<number | undefined> => {
  return axios
    .get(`${CLIENT_SUBSCRIPTIONS_URL}count/`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSubscriptionCount',
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  onError?: (error?: any) => void
): Promise<ClientSubscription | undefined> => {
  // Normalize interval units before POSTing — the backend rejects payloads
  // whose billing interval unit doesn't match the service interval unit.
  const body = normalizePlanIntervals(clientSubscription);
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/`, body)
    .then((response: AxiosResponse<ClientSubscription>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientSubscription',
        data: { clientUuid, clientSubscription, normalizedClientSubscription: body },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const updateClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  onError?: (error?: any) => void
): Promise<ClientSubscription | undefined> => {
  return axios
    .patch(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/`, clientSubscription)
    .then((response: AxiosResponse<ClientSubscription>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClientSubscription',
        data: { clientUuid, clientSubscription },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientSubscriptionLink = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  onError?: (error?: any) => void
): Promise<ClientSubscription | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/get-link/`)
    .then((response: AxiosResponse<ClientSubscription>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSubscriptionLink',
        data: { clientUuid, clientSubscription },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const inviteClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/invite/`, details)
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'inviteClientSubscription',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const resendInviteClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: { email?: string; invite_text?: any },
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/resend_invite/`, details || {})
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'resendInviteClientSubscription',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const pauseClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/pause/`, { details: details })
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'pauseClientSubscription',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const pauseClientSubscriptionPreview = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: any,
  onError?: (error?: any) => void
): Promise<{ cancel_count: number; credit_eligible_count: number } | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/pause_preview/`, { details: details })
    .then((response: AxiosResponse<{ cancel_count: number; credit_eligible_count: number }>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'pauseClientSubscriptionPreview',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const cancelClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/cancel/`, { details: details })
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'cancelClientSubscription',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

/**
 * The `cancel_preview` response. One declaration: the request's return type, its
 * `.then` annotation and `SubscriptionInfo`'s `cancelPreview` state all read it,
 * so a field the server adds can never be visible to one and invisible to the
 * next (WST-891).
 */
export type CancelPreview = {
  supported: boolean;
  reason?: string;
  plan_type?: string;
  subscription_interval?: string;
  subscription_interval_count?: number;
  provider_timezone?: string;
  today?: string;
  end_date?: string | null;
  allowed_end_dates?: Array<string>;
  effective_cancel_at?: string | null;
  session_cancel_start_date?: string | null;
  cancel_sessions_option?: string;
  cycle_recommendations?: {
    timezone?: string;
    cycles?: Array<{ due_date: string; period_start: string; period_end: string }>;
  };
  payment_dates?: Array<string>;
  cycle_end_dates?: Array<string>;
  current_cycle_due_date?: string;
  chargeable_session_count?: number;
  draft_invoice_line_item_count?: number;
  last_cycle?: { due_date?: string | null; period_start: string; period_end: string } | null;
  expected_sessions_to_bill_last_cycle?: number | null;
  expected_remaining_sessions_in_cycle?: number | null;
  // WST-759: prepaid mid-cycle credit selector
  is_mid_cycle_prepaid_cancel?: boolean;
  prepaid_paid_session_count?: number;
  prepaid_unused_session_count?: number;
  prepaid_unit_rate?: number;
  // WST-891 D7: the exact unused value and its per-service breakdown (the unit
  // rate above is only their mean), plus the tuition gate on balance credits.
  prepaid_unused_total_value?: number;
  prepaid_unused_by_service?: Array<{
    provider_service_uuid: string | null;
    service_name: string | null;
    // null on a tuition plan: the period is priced, the sessions are not.
    rate: number | null;
    count: number;
  }>;
  balance_credit_supported?: boolean;
};

const cancelClientSubscriptionPreview = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  details?: any,
  onError?: (error?: any) => void
): Promise<CancelPreview | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/cancel_preview/`, { details: details })
    .then((response: AxiosResponse<CancelPreview>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'cancelClientSubscriptionPreview',
        data: { clientUuid, clientSubscription, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const removeClientSubscription = (
  clientUuid: ID,
  clientSubscription: ClientSubscription,
  onError?: (error?: any) => void,
  cancelSessions: boolean = true
): Promise<boolean> => {
  return axios
    .delete(`${CLIENT_URL}${clientUuid}/subscriptions/${clientSubscription.uuid}/`, {
      params: { cancel_sessions: cancelSessions },
    })
    .then(() => {
      return true;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'removeClientSubscription',
        data: { clientUuid, clientSubscription },
      });
      if (onError) onError(error);
      else throw error;

      return false;
    });
};

const getClientOneOffPayments = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<ClientOneOffPayment> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/oneoff-payments/?${query || ''}`)
    .then((d: AxiosResponse<Array<ClientOneOffPayment>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientOneOffPayments',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getAllClientOneOffPayments = (
  filters?: any,
  onError?: (error?: any) => void
): Promise<Array<ClientOneOffPayment> | undefined> => {
  return axios
    .get(`${API_URL}/client-oneoff-payments/`, { params: filters })
    .then((d: AxiosResponse<Array<ClientOneOffPayment>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getAllClientOneOffPayments',
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientOneOffPayment = (
  clientUuid: ID,
  oneOffUuid: ID,
  onError?: (error?: any) => void
): Promise<ClientOneOffPayment | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/oneoff-payments/${oneOffUuid}/`)
    .then((d: AxiosResponse<ClientOneOffPayment>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientOneOffPayment',
        data: { clientUuid, oneOffUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createClientOneOffPayment = (
  clientUuid: ID,
  clientOneOffPayment: ClientOneOffPayment,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientOneOffPayment | undefined> => {
  const oneOffWithInviteDetails = { 
    ...clientOneOffPayment, 
    invite_details: details, 
    due_date: clientOneOffPayment.due_date
      ? clientOneOffPayment.due_date.toISOString().split('T')[0]
      : undefined
  };
  return axios
    .post(`${CLIENT_URL}${clientUuid}/oneoff-payments/`, oneOffWithInviteDetails)
    .then((response: AxiosResponse<ClientOneOffPayment>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientOneOffPayment',
        data: { clientUuid, clientOneOffPayment, details },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const sendInviteClientOneOffPayment = (
  clientUuid: ID,
  clientOneOffPayment: ClientOneOffPayment,
  email?: string,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/oneoff-payments/${clientOneOffPayment.uuid}/send_invite/`, {
      email: email,
      details: details,
    })
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'resendClientOneOffPayment',
        data: { clientUuid, clientOneOffPayment, email },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const voidOneOffPayment = (props: {
  clientUuid: ID;
  oneOffUuid: ID;
  onError?: (error?: any) => void;
}): Promise<ClientOneOffPayment | undefined> => {
  const { clientUuid, oneOffUuid, onError } = props;
  return axios
    .post(`${CLIENT_URL}${clientUuid}/oneoff-payments/${oneOffUuid}/void/`)
    .then((d: AxiosResponse<ClientOneOffPayment>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'voidOneOffPayment',
        data: { clientUuid, oneOffUuid },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const getClientSessionCredits = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<IClientSessionCredit> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/session-credits/?${query || ''}`)
    .then((d: AxiosResponse<Array<IClientSessionCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSessionCredits',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientSessionCreditCount = (
  clientUuid: ID,
  providerServiceUuid?: ID,
  onError?: (error?: any) => void
): Promise<number | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/session-credits/count/?${providerServiceUuid ? `provider_service=${providerServiceUuid}` : ''}`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSessionCreditCount',
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

function useClientSessionCreditCount(
  clientUuid?: ID,
  providerServiceUuid?: ID,
  enabled: boolean = true,
  onError?: (error?: any) => void
) {
  return useQuery(
    ['client-session-credit-count', clientUuid, providerServiceUuid],
    async (): Promise<number | undefined> => {
      if (!clientUuid) return undefined;
      return await getClientSessionCreditCount(clientUuid, providerServiceUuid, onError);
    },
    {
      enabled: enabled !== false && !!clientUuid,
      // Let react-query refresh on window focus for us
      refetchOnWindowFocus: true,
      onError: (error) => {
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

interface SessionCreditBreakdownItem {
  provider_service_uuid: string | null;
  name: string;
  count: number;
  low: boolean;
}

interface SessionCreditBreakdown {
  total: number;
  breakdown: SessionCreditBreakdownItem[];
}

const getClientSessionCreditBreakdown = (
  clientUuid: ID
): Promise<SessionCreditBreakdown | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/session-credits/breakdown/`)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSessionCreditBreakdown',
      });
      return undefined;
    });
};

function useClientSessionCreditBreakdown(clientUuid?: ID, enabled: boolean = true) {
  return useQuery(
    ['client-session-credit-breakdown', clientUuid],
    async (): Promise<SessionCreditBreakdown | undefined> => {
      if (!clientUuid) return undefined;
      return await getClientSessionCreditBreakdown(clientUuid);
    },
    {
      enabled: enabled !== false && !!clientUuid,
      refetchOnWindowFocus: true,
    }
  );
}

const createClientSessionCredit = (
  clientUuid: ID,
  sessionCredit: IClientSessionCredit
): Promise<Array<IClientSessionCredit> | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/session-credits/`, sessionCredit)
    .then((d: AxiosResponse<Array<IClientSessionCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSessionCredits',
        data: { sessionCredit },
      });
      throw error;
    });
};

const updateClientSessionCredit = (
  clientUuid: ID,
  sessionCredit: IClientSessionCredit
): Promise<Array<IClientSessionCredit> | undefined> => {
  return axios
    .patch(`${CLIENT_URL}${clientUuid}/session-credits/${sessionCredit.uuid}/`, sessionCredit)
    .then((d: AxiosResponse<Array<IClientSessionCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClientSessionCredit',
        data: { sessionCredit },
      });
      throw error;
    });
};

const getClientBalanceCredits = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<IClientBalanceCredit> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/balance-credits/?${query || ''}`)
    .then((d: AxiosResponse<Array<IClientBalanceCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientSessionCredit',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientBalanceCreditCount = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<number | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/balance-credits/count/?${query || ''}`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientBalanceCreditCount',
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const createClientBalanceCredit = (
  clientUuid: ID,
  balanceCredit: IClientBalanceCredit
): Promise<Array<IClientBalanceCredit> | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/balance-credits/`, balanceCredit)
    .then((d: AxiosResponse<Array<IClientBalanceCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientBalanceCredit',
        data: { balanceCredit },
      });
      throw error;
    });
};

const updateClientBalanceCredit = (
  clientUuid: ID,
  balanceCredit: IClientBalanceCredit
): Promise<Array<IClientBalanceCredit> | undefined> => {
  return axios
    .patch(`${CLIENT_URL}${clientUuid}/balance-credits/${balanceCredit.uuid}/`, balanceCredit)
    .then((d: AxiosResponse<Array<IClientBalanceCredit>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClientBalanceCredit',
        data: { balanceCredit },
      });
      throw error;
    });
};

/**
 * WST-878: release a balance credit from the upcoming (DRAFT) invoice. Detach,
 * not delete - the credit stays active and available for future invoices.
 *
 * Rethrows so the caller can tell a retryable 409 (the invoice is mid-charge)
 * from a 400 (the credit is not on an upcoming invoice, or the invoice was
 * manually overridden).
 */
const detachClientBalanceCredit = (
  clientUuid: ID,
  creditUuid: ID
): Promise<IBalanceCreditDetachResponse | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/balance-credits/${creditUuid}/detach/`)
    .then((d: AxiosResponse<IBalanceCreditDetachResponse>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'detachClientBalanceCredit',
        data: { creditUuid },
      });
      throw error;
    });
};

const bulkSessionCreditAction = (
  clientUuid: ID,
  body: { action: BulkActionName; uuids: string[] }
): Promise<{ results: BulkActionResult[] } | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/session-credits/bulk/`, body)
    .then((d) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'client._requests', func: 'bulkSessionCreditAction', data: { body } });
      throw error;
    });
};

const bulkBalanceCreditAction = (
  clientUuid: ID,
  body: { action: BulkActionName; uuids: string[] }
): Promise<{ results: BulkActionResult[] } | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/balance-credits/bulk/`, body)
    .then((d) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'client._requests', func: 'bulkBalanceCreditAction', data: { body } });
      throw error;
    });
};

const getClientInvites = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<ClientInvite> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/invites/?${query ? query : ''}`)
    .then((d: AxiosResponse<Array<ClientInvite>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientInvites',
        data: { clientUuid, query },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const retryPaymentFailure = (invoiceUuid: ID): Promise<{ status?: string; [key: string]: any }> => {
  return axios
    .post(`${API_URL}/payment-failures/${invoiceUuid}/retry/`)
    .then((d: AxiosResponse<{ status?: string; [key: string]: any }>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'retryPaymentFailure',
        data: { invoiceUuid },
      });
      throw error;
    });
};

const updatePaymentMethodForFailure = (invoiceUuid: ID): Promise<{ update_url?: string; [key: string]: any }> => {
  return axios
    .post(`${API_URL}/payment-failures/${invoiceUuid}/update-method/`)
    .then((d: AxiosResponse<{ update_url?: string; [key: string]: any }>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updatePaymentMethodForFailure',
        data: { invoiceUuid },
      });
      throw error;
    });
};
const getClientSuperBills = (
  clientUuid: ID,
  query?: string,
  onError?: (error?: any) => void
): Promise<Array<ISuperBill> | undefined> => {
  return axios
    .get(`${CLIENT_URL}${clientUuid}/superbills/?${query || ''}`)
    .then((d: AxiosResponse<Array<ISuperBill>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientSuperBills',
        data: { clientUuid },
      });
      if (onError) onError(error);
      else throw error;

      return undefined;
    });
};

const getClientContacts = (clientUuid: ID, query?: string): Promise<Array<ClientContact> | undefined> => {
  return axios
    .get(`${CLIENT_CONTACT_URL(clientUuid)}?${query || ''}`)
    .then((d: AxiosResponse<Array<ClientContact>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'getClientContacts',
        data: { query },
      });
      return undefined;
    });
};

const getClientContact = (clientUuid: ID, uuid: ID): Promise<ClientContact | undefined> => {
  return axios
    .get(`${CLIENT_CONTACT_URL(clientUuid)}${uuid}/`)
    .then((d: AxiosResponse<ClientContact>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'getClientContact',
        data: { uuid },
      });
      return undefined;
    });
};

const createClientContact = (client_contact: ClientContact): Promise<ClientContact | undefined> => {
  return axios
    .post(CLIENT_CONTACT_URL(client_contact.client?.uuid), client_contact)
    .then((d: AxiosResponse<ClientContact>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'createClientContact',
        data: { client_contact },
      });
      return undefined;
    });
};

const updateClientContact = (
  client_contact: ClientContact,
  update: ClientContact
): Promise<ClientContact | undefined> => {
  return axios
    .put(`${CLIENT_CONTACT_URL(client_contact.client?.uuid)}${client_contact.uuid}/`, update)
    .then((d: AxiosResponse<ClientContact>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'updateClientContact',
        client_contact: client_contact,
        update: update,
      });
      return undefined;
    });
};

const deleteClientContact = (clientUuid: ID, uuid: ID): Promise<boolean> => {
  return axios
    .delete(`${CLIENT_CONTACT_URL(clientUuid)}${uuid}/`)
    .then(() => true)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'deleteClientContact',
        data: { uuid },
      });
      return false;
    });
};

const createClientContactConsent = (
  clientContact: ClientContact,
  consent: ClientContactConsent
): Promise<ClientContactConsent | undefined> => {
  return axios
    .post(`${CLIENT_CONTACT_URL(clientContact.client?.uuid)}${clientContact.uuid}/consents/`, consent)
    .then((d: AxiosResponse<ClientContactConsent>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'createClientContactConsent',
        data: { clientContactUuid: clientContact.uuid, consent },
      });
      return undefined;
    });
};

const updateClientContactConsent = (
  consent: ClientContactConsent,
  update: ClientContactConsent
): Promise<ClientContactConsent | undefined> => {
  return axios
    .put(
      `${CLIENT_CONTACT_URL(consent.client_contact?.client?.uuid)}${consent.client_contact?.uuid}/consents/${
        consent.uuid
      }/`,
      update
    )
    .then((d: AxiosResponse<ClientContactConsent>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'updateClientContactConsent',
        data: { clientContactUuid: consent.client_contact?.client?.uuid, consentUuid: consent.uuid, consent },
      });
      return undefined;
    });
};

const deleteClientContactConsent = (clientContact: ClientContact, consent: ClientContactConsent): Promise<boolean> => {
  return axios
    .delete(`${CLIENT_CONTACT_URL(clientContact.client?.uuid)}${clientContact.uuid}/consents/${consent.uuid}/`)
    .then(() => true)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'clients._requests',
        func: 'deleteClientContactConsent',
        data: { clientContactUuid: clientContact.uuid, consentUuid: consent.uuid },
      });
      return false;
    });
};

export {
  getClients,
  getClientProviders,
  deleteClientProvider,
  archiveClientProvider,
  reassignClientProvider,
  getClientProviderId,
  deleteSelectedClientProviders,
  archiveSelectedClientProviders,
  bulkUpdateClientProviderTags,
  getClientById,
  useClientById,
  createClient,
  updateClient,
  getClientSiblings,
  linkClientSibling,
  unlinkClientSibling,
  createClientSibling,
  getGuardians,
  deleteGuardian,
  removeClientGuardian,
  deleteSelectedGuardians,
  getGuardianById,
  createGuardian,
  updateGuardian,
  updateClientProvider,
  getClientSubscriptions,
  getAllClientSubscriptions,
  useClientSubscriptions,
  getClientSubscriptionById,
  checkMeteredSessionEligibility,
  getClientSubscriptionCount,
  createClientSubscription,
  updateClientSubscription,
  inviteClientSubscription,
  resendInviteClientSubscription,
  pauseClientSubscription,
  pauseClientSubscriptionPreview,
  cancelClientSubscriptionPreview,
  cancelClientSubscription,
  removeClientSubscription,
  reassignClientSubscription,
  getClientOneOffPayments,
  getAllClientOneOffPayments,
  getClientOneOffPayment,
  createClientOneOffPayment,
  sendInviteClientOneOffPayment,
  voidOneOffPayment,
  getClientSessionCredits,
  getClientSessionCreditCount,
  useClientSessionCreditCount,
  getClientSessionCreditBreakdown,
  useClientSessionCreditBreakdown,
  createClientSessionCredit,
  updateClientSessionCredit,
  getClientBalanceCredits,
  getClientBalanceCreditCount,
  createClientBalanceCredit,
  updateClientBalanceCredit,
  detachClientBalanceCredit,
  bulkSessionCreditAction,
  bulkBalanceCreditAction,
  getClientInvites,
  retryPaymentFailure,
  updatePaymentMethodForFailure,
  getClientSuperBills,
  getClientContacts,
  getClientContact,
  createClientContact,
  updateClientContact,
  deleteClientContact,
  createClientContactConsent,
  updateClientContactConsent,
  deleteClientContactConsent,
  getClientActivities,
  createClientActivity,
  updateClientActivity,
  deleteClientActivity,
  resendActivityInvite,
  useClientActivities,
};


// ── Client Activities ─────────────────────────────────────────────────

const getClientActivities = (
  clientUuid: ID,
  params?: { category?: string; search?: string },
  onError?: (error?: any) => void
): Promise<Array<ClientActivityEntry> | undefined> => {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.search) query.set('search', params.search);
  return axios
    .get(`${CLIENT_URL}${clientUuid}/activities/?${query.toString()}`)
    .then((d: AxiosResponse<Array<ClientActivityEntry>>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'getClientActivities',
        data: { clientUuid, params },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

function useClientActivities(
  clientUuid?: ID,
  params?: { category?: string; search?: string },
  enabled: boolean = true,
  onError?: (error?: any) => void
) {
  return useQuery(
    ['client-activities', clientUuid, params?.category, params?.search],
    async (): Promise<Array<ClientActivityEntry> | undefined> => {
      if (!clientUuid) return undefined;
      return await getClientActivities(clientUuid, params, onError);
    },
    {
      enabled: enabled !== false && !!clientUuid,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'client._requests',
          func: 'useClientActivities',
          data: { clientUuid },
        });
        if (onError) onError(error);
      },
    }
  );
}

const createClientActivity = (
  clientUuid: ID,
  activity: Partial<ClientActivityEntry> & { activity_date?: string },
  onError?: (error?: any) => void
): Promise<ClientActivityEntry | undefined> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/activities/`, activity)
    .then((d: AxiosResponse<ClientActivityEntry>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'createClientActivity',
        data: { clientUuid, activity },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const updateClientActivity = (
  clientUuid: ID,
  activityUuid: ID,
  data: Partial<ClientActivityEntry>,
  onError?: (error?: any) => void
): Promise<ClientActivityEntry | undefined> => {
  return axios
    .patch(`${CLIENT_URL}${clientUuid}/activities/${activityUuid}/`, data)
    .then((d: AxiosResponse<ClientActivityEntry>) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'updateClientActivity',
        data: { clientUuid, activityUuid },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const deleteClientActivity = (
  clientUuid: ID,
  activityUuid: ID,
  onError?: (error?: any) => void
): Promise<void> => {
  return axios
    .delete(`${CLIENT_URL}${clientUuid}/activities/${activityUuid}/`)
    .then(() => {})
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'deleteClientActivity',
        data: { clientUuid, activityUuid },
      });
      if (onError) onError(error);
      else throw error;
    });
};

const resendActivityInvite = (
  clientUuid: ID,
  activityUuid: ID,
  email?: string,
  onError?: (error?: any) => void
): Promise<any> => {
  return axios
    .post(`${CLIENT_URL}${clientUuid}/activities/${activityUuid}/resend-invite/`, { email })
    .then((d: AxiosResponse) => d.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'resendActivityInvite',
        data: { clientUuid, activityUuid },
      });
      if (onError) onError(error);
      else throw error;
    });
};

// ── Client Import ──────────────────────────────────────────────────

export interface BulkImportResult {
  row: number;
  status: 'matched' | 'created' | 'skipped' | 'error';
  client_name?: string;
  provider_name?: string;
  message?: string;
}

export interface BulkImportStatus {
  uuid: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  import_type: string;
  file_name: string;
  total_records: number;
  matched_count: number;
  created_count: number;
  skipped_count: number;
  error_count: number;
  error_message?: string;
  results: BulkImportResult[];
  created_at: string;
  updated_at: string;
}

export const importClientList = (file: File): Promise<BulkImportStatus> => {
  const formData = new FormData();
  formData.append('file', file);
  return axios
    .post(`${CLIENT_URL}import-clients/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((response: AxiosResponse<BulkImportStatus>) => response.data);
};

export const getClientImportStatus = (uuid: string): Promise<BulkImportStatus> => {
  return axios
    .get(`${CLIENT_URL}import-status/?uuid=${uuid}`)
    .then((response: AxiosResponse<BulkImportStatus>) => response.data);
};
